/* eslint-disable no-bitwise, no-plusplus, no-undef */
/**
 * Password Hashing Utility
 * Hashes passwords with SHA-256 and Base64-encodes the result to match backend format.
 *
 * ⚠️ SECURITY NOTE:
 * - This is CLIENT-SIDE TRANSPORT OBFUSCATION only, NOT a substitute for server-side password storage
 * - Backend MUST re-hash with proper KDF (bcrypt/argon2/scrypt) + unique salt per user
 * - Plain SHA-256 is intentionally weak (no salt, fast) - suitable only for transmission over TLS
 * - HTTPS/TLS is MANDATORY - client-side hashing does NOT replace transport security
 *
 * Uses native Web Crypto API (browser standard, zero dependencies)
 * Falls back to pure JS SHA-256 for HTTP contexts (development/staging only)
 */

/**
 * Converts ArrayBuffer to Base64 string
 * @param {ArrayBuffer} buffer - Hash buffer from crypto.subtle.digest
 * @returns {string} Base64-encoded string
 */
const arrayBufferToBase64 = (buffer) => {
	const bytes = new Uint8Array(buffer);
	const binary = String.fromCharCode(...bytes);
	return btoa(binary);
};

/**
 * Pure JavaScript SHA-256 implementation (lightweight, ~2KB)
 * Only used as fallback for HTTP contexts where Web Crypto API is unavailable
 * Based on standard SHA-256 algorithm
 */
const sha256Pure = (message) => {
	const K = [
		0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
		0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
		0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
		0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
		0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
		0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
		0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
		0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
	];

	const rotr = (n, x) => (x >>> n) | (x << (32 - n));
	const bytes = new TextEncoder().encode(message);
	const ml = bytes.length * 8;
	const paddingLength = (((ml + 64) >>> 9) << 4) + 15;
	const blocks = new Uint32Array(paddingLength + 1);

	// Copy message bytes
	for (let i = 0; i < bytes.length; i++) {
		blocks[i >>> 2] |= bytes[i] << (24 - (i % 4) * 8);
	}

	// Append 1 bit
	blocks[bytes.length >>> 2] |= 0x80 << (24 - (bytes.length % 4) * 8);

	// Append message length
	blocks[paddingLength] = ml;

	// Initialize hash values
	let h0 = 0x6a09e667; let h1 = 0xbb67ae85; let h2 = 0x3c6ef372; let
h3 = 0xa54ff53a;
	let h4 = 0x510e527f; let h5 = 0x9b05688c; let h6 = 0x1f83d9ab; let
h7 = 0x5be0cd19;

	const w = new Uint32Array(64);

	// Process blocks
	for (let i = 0; i < blocks.length; i += 16) {
		let a = h0; let b = h1; let c = h2; let d = h3; let e = h4; let f = h5; let g = h6; let
h = h7;

		for (let j = 0; j < 64; j++) {
			if (j < 16) {
				w[j] = blocks[i + j] >>> 0;
			} else {
				const s0 = rotr(7, w[j - 15]) ^ rotr(18, w[j - 15]) ^ (w[j - 15] >>> 3);
				const s1 = rotr(17, w[j - 2]) ^ rotr(19, w[j - 2]) ^ (w[j - 2] >>> 10);
				w[j] = (w[j - 16] + s0 + w[j - 7] + s1) >>> 0;
			}

			const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
			const ch = (e & f) ^ (~e & g);
			const temp1 = (h + S1 + ch + K[j] + w[j]) >>> 0;
			const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
			const maj = (a & b) ^ (a & c) ^ (b & c);
			const temp2 = (S0 + maj) >>> 0;

			h = g; g = f; f = e;
			e = (d + temp1) >>> 0;
			d = c; c = b; b = a;
			a = (temp1 + temp2) >>> 0;
		}

		h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0;
		h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
		h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0;
		h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
	}

	// Convert to byte array
	const hash = new Uint8Array(32);
	[h0, h1, h2, h3, h4, h5, h6, h7].forEach((h, i) => {
		hash[i * 4] = (h >>> 24) & 0xff;
		hash[i * 4 + 1] = (h >>> 16) & 0xff;
		hash[i * 4 + 2] = (h >>> 8) & 0xff;
		hash[i * 4 + 3] = h & 0xff;
	});

	return arrayBufferToBase64(hash.buffer);
};

/**
 * Hashes password with SHA-256 and returns Base64-encoded result
 *
 * Equivalent to Java backend implementation:
 * ```java
 * byte[] data = text.getBytes(StandardCharsets.UTF_8);
 * MessageDigest digester = MessageDigest.getInstance("SHA-256");
 * digester.update(data);
 * return Base64.getEncoder().encodeToString(digester.digest());
 * ```
 *
 * @param {string} password - Plain text password to hash
 * @returns {Promise<string>} Base64-encoded SHA-256 hash
 * @throws {Error} If password is invalid
 *
 * @example
 * const hash = await hashPassword('myPassword123');
 * // Returns: "XohImNooBHFR0OVvjcYpJ3NgPQ1qq73WKhHvch0VQtg=" (Base64 format)
 */
export const hashPassword = async (password) => {
	if (typeof password !== 'string' || password.length === 0) {
		throw new Error('Invalid input: password must be a non-empty string');
	}

	try {
		// Try Web Crypto API first (available on HTTPS and localhost)
		if (globalThis.crypto?.subtle) {
			const encoder = new TextEncoder();
			const data = encoder.encode(password);
			const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
			return arrayBufferToBase64(hashBuffer);
		}

		// Fallback to pure JS SHA-256 for HTTP contexts (development/staging)
		console.warn('⚠️ Web Crypto API not available (HTTP context). Using pure JS fallback. Deploy to HTTPS for production!');
		return sha256Pure(password);
	} catch (error) {
		throw new Error(`Failed to hash password: ${error.message}`);
	}
};
